import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import css from './Widget.css';
import MaterialIcon from "../SF/MaterialIcon";
import {useLocalStorage} from "react-use";
import CalendarContainer from "./CalendarContainer";
import Notifications from "./Notifications";
import Kudos from "./Kudos";
import {successNetworkUrl} from "../utils/successNetworkURL";
import Footer from "./Footer";
import ConnectionList from "./ConnectionList";
import ServiceList from "./ServiceList";
import {RequestHelp} from "utils/types";

/**
 * Array containing the widgets supporting the my success network footer.
 * @type {string[]}
 */
const widgetsWithFooter = ['connection', 'services']

/**
 * The container for the Student Dashboard widget
 * @param {object} props Component props
 * @returns {React.Component} a React component containing a Student Dashboard widget.
 */
const Widget = (props) => {
  const [count, setCount] = useState(null)

  // Screen size dimensions
  const isMedium = props.screenWidth > 728 && props.screenWidth < 1440;
  const isLarge = props.screenWidth > 1439;

  const [lastWidgetState, setLastWidgetState] = useLocalStorage(props.type + 'WidgetOpen', null);

  const getWidgetInitialState = widgetState => {
    if ( isLarge ) return true; // in large screens, it's always opened by default
    if ( widgetState !== null ) return widgetState; // if there's anything in the local storage, return that
    if ( isMedium ) return true; // in medium screens, it starts opened unless the user has closed it before
    return false; // in small screens, it starts closed unless the user has opened it before
  };

  const [widgetOpen, setWidget] = useState(getWidgetInitialState(lastWidgetState));

  useEffect(() => {
    setLastWidgetState(widgetOpen);
  }, [widgetOpen]);

  const onToggle = (() => {
    setWidget(!widgetOpen);
  });

  // Always display content in large screens, no matter what. For other screen sizes,
  // check what is the widget state
  const displayContent = isLarge || widgetOpen;

  const widgetClasses = [css.widget];
  if ( props.type === 'kudos' && !count ) {
    widgetClasses.push(css.hideContent);
  }
  if ( props.type === 'services' && props.hideServices ) {
    widgetClasses.push(css.hideContent);
  }

  const getContent = () => {
    switch (props.type) {
      case 'calendar': return <CalendarContainer successToastMessage={props.successToastMessage}
                                                 setError={props.setError}
                                                 requestHelp={props.requestHelp}/>
      case 'notifications': return <Notifications successToastMessage={props.successToastMessage}
                                                  setCount={setCount}
                                                  setError={props.setError}
                                                  studentId={props.studentId}
                                                  requestHelp={props.requestHelp}/>
      case 'kudos': return <Kudos setCount={setCount} />
      case 'connection': return <ConnectionList setCount={setCount} setError={props.setError}/>;
      case 'services': return <ServiceList setCount={setCount}
                                           setError={props.setError}
                                           responsiveKioskEnabled={props.responsiveKioskEnabled}
                                           virtualCheckInEnabled={props.virtualCheckInEnabled}/>;
      default: return null
    }
  }

  const getFooter = () => {
    // Never display footer if content is hidden
    if (!displayContent) return null;

    // Not all widgets have footers
    if (widgetsWithFooter.indexOf(props.type) === -1) return null;

    return <div className={css.footer}><Footer text={props.successNetworkText} url={successNetworkUrl} /></div>;
  }

  return (
    <div className={widgetClasses.join(" ")} aria-label='dashboard widget' data-role={props.type} ref={props.scrollRef}>
      <div className={css.header}>
        <h2>
          <MaterialIcon aria-hidden="true" icon={props.icon}/> <span className={css.title}>{props.title}</span>
          {typeof count === 'number' && (
            <div className={css.count} data-testid={props.type + "Count"}>
              ({count})
            </div>
          )}
        </h2>
        <button
          aria-label={`${props.title} Widget toggle button`}
          aria-expanded={widgetOpen}
          className={css.button}
          onClick={() => onToggle()}>
          <MaterialIcon
            className={css.arrow}
            aria-hidden="true"
            icon={displayContent ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}/>
        </button>
      </div>
      <div className={css.content}
           aria-label='dashboard widget content'
           style={{
             opacity: displayContent ? 1 : 0,
             height: displayContent ? '100%' : 0
           }}>
        { getContent() }
      </div>
        { getFooter() }
    </div>
  );
};

Widget.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  screenWidth: PropTypes.number.isRequired,
  scrollRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),
  setError: PropTypes.func.isRequired,
  studentId: PropTypes.number.isRequired,
  successNetworkText: PropTypes.string.isRequired,
  successToastMessage: PropTypes.func.isRequired,
  hideServices: PropTypes.bool,
  responsiveKioskEnabled: PropTypes.bool,
  virtualCheckInEnabled: PropTypes.bool,
  requestHelp: RequestHelp.isRequired
};

export default Widget;
